import _ from 'lodash';
import { t } from '@lingui/macro';

import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    VOIP_MODE_NONE,
    VOIP_MODE_PRE_AUTO,
    VOIP_MODE_PRE_MANUAL,
    VOIP_REGISTRATION_STATUS_ERROR,
    VOIP_REGISTRATION_STATUS_PENDING,
} from '../agents-voip/AgentVoIPConstants';
import { isHardware } from '../agents/AgentHelper';
import { VOIP_ADAPTER_TYPE, VOIP_CALL_MANAGER_TYPE_CUCM, VOIP_TOS_CODE, VOIP_QOS_MODE } from './VoipConstants';

const VVLAN_MODE_NONE = 'N';

export const certColumns = [
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Name`,
        sortable: true,
        mapping: 'name',
        width: 160,
    },
    {
        type: COLUMN_TYPES.TEXT,
        title: t`Description`,
        sortable: true,
        mapping: 'description',
        width: 360,
    },
];

export function buildQosSelection(voipConfiguration) {
    const qos = voipConfiguration?.qos;
    if (!qos) {
        return VOIP_QOS_MODE.default;
    }
    if (!_.isNil(qos.dscpCode)) {
        return VOIP_QOS_MODE.dscp;
    }
    if (!_.isNil(qos.ipPrecedenceCode) || !_.isNil(qos.tosCode)) {
        return VOIP_QOS_MODE.ip;
    }
    return VOIP_QOS_MODE.default;
}

export function convertQosSelection(voipConfiguration, qosSelection) {
    if (!voipConfiguration) {
        return;
    }

    if (qosSelection === VOIP_QOS_MODE.default) {
        voipConfiguration.qos = null;
        return;
    }

    if (!voipConfiguration.qos) {
        voipConfiguration.qos = {};
    }

    let calculated = 0;
    if (qosSelection === VOIP_QOS_MODE.dscp) {
        voipConfiguration.qos.ipPrecedenceCode = null;
        voipConfiguration.qos.tosCode = null;
        const dscpCode = parseInt(voipConfiguration.qos.dscpCode || 0, 10);
        calculated = dscpCode * 4;
    }

    if (qosSelection === VOIP_QOS_MODE.ip) {
        voipConfiguration.qos.dscpCode = null;
        const ipPrecedence = parseInt(voipConfiguration.qos.ipPrecedenceCode || 0, 10);
        calculated = ipPrecedence * 32;

        if (voipConfiguration.qos.tosCode === VOIP_TOS_CODE.delay) {
            // eslint-disable-next-line no-bitwise
            calculated |= 0x10;
        }
        if (voipConfiguration.qos.tosCode === VOIP_TOS_CODE.throughput) {
            // eslint-disable-next-line no-bitwise
            calculated |= 0x08;
        }
        if (voipConfiguration.qos.tosCode === VOIP_TOS_CODE.reliability) {
            // eslint-disable-next-line no-bitwise
            calculated |= 0x04;
        }
    }

    voipConfiguration.qos.l3Priority = calculated;
}

export function isManual(voipMode) {
    return voipMode === VOIP_MODE_PRE_MANUAL;
}

export function isAuto(voipMode) {
    return voipMode === VOIP_MODE_PRE_AUTO;
}

export function isConfigured(voipMode) {
    return voipMode && voipMode !== VOIP_MODE_NONE;
}

export function getCmLabelsForCallManagerType(cmType) {
    if (cmType === VOIP_CALL_MANAGER_TYPE_CUCM) {
        return {
            phoneNumber: t`Phone Number`,
            extension: t`Extension`,
            sipUserId: t`PIN`,
            authName: t`User ID`,
            password: t`Password`,
        };
    }

    return {
        phoneNumber: t`Phone Number`,
        extension: t`Extension`,
        sipUserId: t`SIP User ID`,
        authName: t`Authorization Name`,
        password: t`Password`,
    };
}
export function defaultVlan(putData) {
    const adapterType = _.get(putData, 'voipConfiguration.adapterType');
    const hasVlanMode = !_.isEmpty(_.get(putData, 'voipConfiguration.vvlan.mode'));
    putData.voipConfiguration.vvlan ??= {};
    if (
        (isHardware(putData) && isManual(putData.voipMode) && !hasVlanMode) ||
        adapterType === VOIP_ADAPTER_TYPE.wireless
    ) {
        _.set(putData, 'voipConfiguration.vvlan.mode', VVLAN_MODE_NONE);
    }
}
export function prepareVoipAgentSaveData({ formData, submitData = {}, callManagers = [], qosSelection }) {
    const putData = {
        ...formData,
        ...submitData,
    };
    const existingSipUserId = _.get(formData, 'voipConfiguration.sipUserId', null);

    const putVoipMode = putData.voipMode;
    if (!isConfigured(putVoipMode) || isAuto(putVoipMode)) {
        putData.voipConfiguration = null;
        return putData;
    }

    if (isManual(putVoipMode)) {
        _.defaults(putData.voipConfiguration, { ipVersion: 'ipv4', adapterType: VOIP_ADAPTER_TYPE.default });

        convertQosSelection(putData.voipConfiguration, qosSelection);

        defaultVlan(putData);

        _.set(putData, 'voipConfiguration.registration', {
            detail: null,
            error: null,
            status: VOIP_REGISTRATION_STATUS_PENDING,
            updatedAt: null,
        });
    }

    if (_.isNil(putData.voipConfiguration.sipUserId)) {
        putData.voipConfiguration.sipUserId = existingSipUserId;
    }

    if (putData.voipConfiguration.primaryCallManager) {
        const currentCm = callManagers.find((cm) => cm._id === putData.voipConfiguration.primaryCallManager);
        putData.voipConfiguration.securityMode = currentCm?.enableSecurity ? 'Enabled' : '';

        if (currentCm?.cmType === VOIP_CALL_MANAGER_TYPE_CUCM) {
            putData.voipConfiguration.sipUserId = putData.voipConfiguration.phoneNumber || null;
        }
    }

    if (putData.voipConfiguration.useDirectCalling) {
        putData.voipConfiguration.sipUserId = null;
        putData.voipConfiguration.primaryCallManager = null;
        putData.voipConfiguration.extension = null;
        putData.voipConfiguration.sipDomain = null;
    }
    if (!putData.isWirelessEnabled && putData.voipConfiguration.adapterType === VOIP_ADAPTER_TYPE.wireless) {
        _.set(putData, 'voipConfiguration.registration.status', VOIP_REGISTRATION_STATUS_ERROR);
    }

    return putData;
}
